import UiButton from "../../../components/button/Button";
import { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import UiFieldMessage from "../../../components/field-message/FieldMessage.component";
import { FieldMessageSeverity } from "../../../models/components/field-message.model";
import { useMedicalReport } from "../../../hooks/useMedicalReport";
import { Formatter } from "../../../services/formatter/formatter.service";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useIsParticipantSyncableToMetriport } from "../../../hooks/useIsParticipantSyncableToMetriport";
import { BiCopy } from "react-icons/bi";
import { FaCheck } from "react-icons/fa";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";

const POLLING_INTERVAL_FOR_REPORT_IN_MS = 5000;
const TIMEOUT_FOR_REPORT_GENERATION_IN_MS = 40 * 60 * 1000;
// Above this character count, ReactMarkdown's tokenization becomes pathologically slow on
// very long single lines (e.g. verbatim call transcripts) and freezes the browser tab.
// Tunable. Measured freeze case was ~280,000 chars; normal summaries are far smaller.
const PATIENT_SUMMARY_RENDER_THRESHOLD_CHARS = 100_000;

const ParticipantMetriport = ({ participantId }: { participantId: number }) => {
	const { t } = useTranslation("common");
	const [isVisible, setIsVisible] = useState(false);
	const [isPatientSummaryVisible, setIsPatientSummaryVisible] = useState(false);

	return (
		<div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
			<UiButton
				className="p-button-rounded p-button-sm"
				label={t("PARTICIPANT.DASHBOARD.METRIPORT.GET_REPORT")}
				onClick={() => setIsVisible(true)}
			/>
			<UiButton
				className="p-button-rounded p-button-sm"
				label={t("PARTICIPANT.DASHBOARD.METRIPORT.VIEW_PATIENT_SUMMARY")}
				onClick={() => setIsPatientSummaryVisible(true)}
			/>

			{isVisible && (
				<MedicalReportModal
					participantId={participantId}
					visible={isVisible}
					onClose={() => setIsVisible(false)}
				/>
			)}
			{isPatientSummaryVisible && (
				<PatientSummaryModal
					participantId={participantId}
					visible={isPatientSummaryVisible}
					onClose={() => setIsPatientSummaryVisible(false)}
				/>
			)}
		</div>
	);
};

const MedicalReportModal = ({
	participantId,
	visible,
	onClose,
}: {
	participantId: number;
	visible: boolean;
	onClose: () => void;
}) => {
	const { t } = useTranslation("common");
	const [isLoadingContent, setIsLoadingContent] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [isGenerating, setIsGenerating] = useState(false);

	const generationDetails = useRef({
		startTime: 0,
		originalReportUpdatedOn: null as string | null,
	});

	const medicalReportQuery = useMedicalReport({
		participantId,
		includeContent: false,
		refetchInterval: POLLING_INTERVAL_FOR_REPORT_IN_MS,
	});

	// Store the initial report ID when data is first loaded and check generation status
	useEffect(() => {
		checkGenerationStatus();

		if (medicalReportQuery.data && !generationDetails.current.originalReportUpdatedOn) {
			generationDetails.current.originalReportUpdatedOn =
				medicalReportQuery.data.updated_on ?? null;
		}
	}, [medicalReportQuery]);

	/**
	 * Checks if report generation is complete or has timed out
	 * - Detects if a new report is available (ID changed)
	 * - Checks if timeout has passed
	 */
	const checkGenerationStatus = () => {
		if (!isGenerating) return;

		const { startTime, originalReportUpdatedOn } = generationDetails.current;

		const hasNewReport =
			medicalReportQuery.data &&
			medicalReportQuery.data.updated_on !== originalReportUpdatedOn;

		if (hasNewReport) {
			setIsGenerating(false);
			return;
		}

		const elapsedTime = Date.now() - startTime;
		if (startTime > 0 && elapsedTime > TIMEOUT_FOR_REPORT_GENERATION_IN_MS) {
			setIsGenerating(false);
			setError(t("PARTICIPANT.DASHBOARD.METRIPORT.NO_DATA_FOUND"));
		}
	};

	const handleGenerateClick = async () => {
		setIsGenerating(true);
		setError(null);

		generationDetails.current = {
			startTime: Date.now(),
			originalReportUpdatedOn: medicalReportQuery.data?.updated_on || null,
		};

		try {
			await EndpointsService.metriport.generateMedicalReport(participantId);
		} catch (err) {
			setIsGenerating(false);

			if (axios.isAxiosError(err) && err.response?.status === 409) {
				setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_CREATE_PATIENT"));
			} else {
				setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_GENERATE"));
			}
		}
	};

	const handleViewClick = async () => {
		await handleClick({
			onSuccess: (reportWithContent) => {
				if (reportWithContent && reportWithContent.content) {
					const blob = Formatter.base64ToBlob(
						reportWithContent.content,
						"application/pdf"
					);
					Formatter.openFile(blob, "application/pdf");
				} else {
					setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_LOAD"));
				}
			},
		});
	};

	const handleClick = async ({ onSuccess }: { onSuccess: (content: any) => void }) => {
		if (!medicalReportQuery.data) return;

		setIsLoadingContent(true);
		setError(null);

		try {
			const reportWithContent = await EndpointsService.metriport.getMedicalReport({
				participantId,
				includeContent: true,
			});

			onSuccess(reportWithContent);
		} catch (error) {
			setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_LOAD"));
		} finally {
			setIsLoadingContent(false);
		}
	};

	const handleDownloadModifiedJson = async () => {
		await handleClick({
			onSuccess: (reportWithContent) => {
				if (reportWithContent && reportWithContent.modified_json_content) {
					const blob = new Blob([reportWithContent.modified_json_content], {
						type: "application/json",
					});

					// Create download link
					const url = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.download = `modified_json_${participantId}_${new Date().toISOString().split("T")[0]}.json`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);
				} else {
					setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_LOAD"));
				}
			},
		});
	};

	const handleDownloadUnifiedPatientView = async () => {
		await handleClick({
			onSuccess: (reportWithContent) => {
				if (reportWithContent && reportWithContent.unified_patient_view_content) {
					const blob = new Blob([reportWithContent.unified_patient_view_content], {
						type: "application/json",
					});

					const url = window.URL.createObjectURL(blob);
					const link = document.createElement("a");
					link.href = url;
					link.download = `unified_patient_view_${participantId}_${
						new Date().toISOString().split("T")[0]
					}.json`;
					document.body.appendChild(link);
					link.click();
					document.body.removeChild(link);
					window.URL.revokeObjectURL(url);
				} else {
					setError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_LOAD"));
				}
			},
		});
	};

	const patientHasAllNecessaryData = useIsParticipantSyncableToMetriport();

	if (!patientHasAllNecessaryData) {
		return (
			<ModalLayout
				visible={visible}
				onClose={onClose}
			>
				<div>{t("PARTICIPANT.DASHBOARD.METRIPORT.PROFILE_INCOMPLETE")}</div>
				<div>{t("PARTICIPANT.DASHBOARD.METRIPORT.PROFILE_REQUIREMENTS")}</div>
				<div className="participant-metriport__buttons">
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.GENERATE")}
						onClick={handleGenerateClick}
						disabled
					/>
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.VIEW")}
						disabled
					/>
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.DOWNLOAD_MODIFIED_JSON")}
						onClick={handleDownloadModifiedJson}
						disabled
					/>
				</div>
			</ModalLayout>
		);
	}

	if (medicalReportQuery.isLoading) {
		return (
			<ModalLayout
				visible={visible}
				onClose={onClose}
			>
				<UiFieldMessage
					severity={FieldMessageSeverity.LOADING}
					label={t("PARTICIPANT.DASHBOARD.METRIPORT.LOADING_REPORT")}
				/>
			</ModalLayout>
		);
	}

	if (medicalReportQuery.data) {
		const pdfDate = medicalReportQuery.data.created_on
			? Formatter.dateISOToString(medicalReportQuery.data.created_on, {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				})
			: null;

		const jsonDate = medicalReportQuery.data.json_created_on
			? Formatter.dateISOToString(medicalReportQuery.data.json_created_on, {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				})
			: null;

		const upvDate = medicalReportQuery.data.upv_created_on
			? Formatter.dateISOToString(medicalReportQuery.data.upv_created_on, {
					day: "2-digit",
					month: "2-digit",
					year: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				})
			: null;

		const isDownloandModifiedJsonDisabled =
			isLoadingContent || isGenerating || !medicalReportQuery.data.json_created_on;

		return (
			<ModalLayout
				visible={visible}
				onClose={onClose}
			>
				<div>
					<div>
						{t("PARTICIPANT.DASHBOARD.METRIPORT.LAST_REPORT")} {pdfDate}
					</div>
					<div>
						{jsonDate && (
							<>
								{t("PARTICIPANT.DASHBOARD.METRIPORT.LAST_REPORT_JSON")} {jsonDate}
							</>
						)}
					</div>
					<div>
						{upvDate && (
							<>
								{t("PARTICIPANT.DASHBOARD.METRIPORT.LAST_REPORT_UPV")} {upvDate}
							</>
						)}
					</div>
				</div>
				<div className="participant-metriport__buttons">
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.REGENERATE")}
						onClick={handleGenerateClick}
						disabled={isLoadingContent || isGenerating}
					/>
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.VIEW")}
						onClick={handleViewClick}
						disabled={isLoadingContent || isGenerating}
					/>
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.DOWNLOAD_MODIFIED_JSON")}
						onClick={handleDownloadModifiedJson}
						disabled={isDownloandModifiedJsonDisabled}
					/>
					<UiButton
						className="p-button-rounded p-button-sm"
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.DOWNLOAD_UNIFIED_PATIENT_VIEW")}
						onClick={handleDownloadUnifiedPatientView}
						disabled={
							isLoadingContent ||
							isGenerating ||
							!medicalReportQuery.data?.has_unified_patient_view
						}
					/>
				</div>
				{isLoadingContent && (
					<div className="participant-metriport__message-wrapper">
						<UiFieldMessage
							severity={FieldMessageSeverity.LOADING}
							label={t("PARTICIPANT.DASHBOARD.METRIPORT.LOADING_CONTENT")}
						/>
					</div>
				)}
				{isGenerating && (
					<div className="participant-metriport__message-wrapper">
						<UiFieldMessage
							severity={FieldMessageSeverity.LOADING}
							label={t("PARTICIPANT.DASHBOARD.METRIPORT.GENERATING_REPORT")}
						/>
					</div>
				)}
				{error && (
					<div className="participant-metriport__message-wrapper">
						<UiFieldMessage
							severity={FieldMessageSeverity.DANGER}
							label={error}
						/>
					</div>
				)}
			</ModalLayout>
		);
	}

	return (
		<ModalLayout
			visible={visible}
			onClose={onClose}
		>
			<div>{t("PARTICIPANT.DASHBOARD.METRIPORT.NO_REPORT")}</div>
			<div className="participant-metriport__buttons">
				<UiButton
					className="p-button-rounded p-button-sm"
					label={t("PARTICIPANT.DASHBOARD.METRIPORT.GENERATE")}
					onClick={handleGenerateClick}
					disabled={isGenerating}
				/>
				<UiButton
					className="p-button-rounded p-button-sm"
					label={t("PARTICIPANT.DASHBOARD.METRIPORT.VIEW")}
					disabled
				/>
				<UiButton
					className="p-button-rounded p-button-sm"
					label={t("PARTICIPANT.DASHBOARD.METRIPORT.DOWNLOAD_MODIFIED_JSON")}
					disabled
				/>
			</div>
			{isGenerating && (
				<div className="participant-metriport__message-wrapper">
					<UiFieldMessage
						severity={FieldMessageSeverity.LOADING}
						label={t("PARTICIPANT.DASHBOARD.METRIPORT.GENERATING_REPORT")}
					/>
				</div>
			)}
			{error && (
				<div className="participant-metriport__message-wrapper">
					<UiFieldMessage
						severity={FieldMessageSeverity.DANGER}
						label={error}
					/>
				</div>
			)}
		</ModalLayout>
	);
};

export const PatientSummaryModal = ({
	participantId,
	visible,
	onClose,
}: {
	participantId: number;
	visible: boolean;
	onClose: () => void;
}) => {
	const { t } = useTranslation("common");
	const [upvContent, setUpvContent] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	const [copied, setCopied] = useState(false);
	const [copyError, setCopyError] = useState<string | null>(null);

	useEffect(() => {
		if (!visible) return;

		let cancelled = false;
		setLoadError(null);
		setUpvContent(null);
		setIsLoading(true);

		EndpointsService.metriport
			.getGenerateUpv(participantId)
			.then((response) => {
				if (cancelled) return;
				let raw = typeof response?.content === "string" ? response.content.trim() : "";
				// Normalize literal \n (backslash-n) to real newlines so line breaks display
				if (raw) raw = raw.replace(/\\n/g, "\n");
				setUpvContent(raw || "");
			})
			.catch(() => {
				if (!cancelled)
					setLoadError(t("PARTICIPANT.DASHBOARD.METRIPORT.ERROR_UNKNOWN_LOAD"));
			})
			.finally(() => {
				if (!cancelled) setIsLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [visible, participantId, t]);

	const handleCopy = async () => {
		setCopyError(null);
		if (!upvContent) return;
		try {
			await navigator.clipboard.writeText(upvContent);
			setCopied(true);
			setTimeout(() => setCopied(false), 3000);
		} catch {
			setCopyError("PARTICIPANT.DASHBOARD.METRIPORT.PATIENT_SUMMARY_COPY_ERROR");
		}
	};

	const header = (
		<div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
			<span>{t("PARTICIPANT.DASHBOARD.METRIPORT.PATIENT_SUMMARY_MODAL_TITLE")}</span>
			{upvContent && !isLoading && (
				<>
					{!copied ? (
						<UiButton
							className="p-button-outlined p-button-sm"
							label={t("UI_COMPONENTS.BUTTONS.COPY")}
							icon={<BiCopy />}
							onClick={handleCopy}
						/>
					) : (
						<UiButton
							className="p-button-outlined p-button-sm"
							label={t("UI_COMPONENTS.BUTTONS.COPIED")}
							icon={<FaCheck />}
							style={{ pointerEvents: "none" }}
						/>
					)}
				</>
			)}
		</div>
	);

	return (
		<Dialog
			header={header}
			visible={visible}
			breakpoints={{ "960px": "60vw", "640px": "100vw" }}
			style={{ width: "60vw" }}
			modal
			draggable={false}
			resizable={false}
			focusOnShow={false}
			onHide={onClose}
		>
			{isLoading && (
				<UiFieldMessage
					severity={FieldMessageSeverity.LOADING}
					label={t("PARTICIPANT.DASHBOARD.METRIPORT.LOADING_CONTENT")}
				/>
			)}
			{loadError && (
				<UiFieldMessage
					severity={FieldMessageSeverity.DANGER}
					label={loadError}
				/>
			)}
			{!isLoading && !loadError && upvContent !== null && (
				<>
					{copyError && (
						<div className="mb-2">
							<UiFieldMessage
								severity={FieldMessageSeverity.DANGER}
								label={copyError}
							/>
						</div>
					)}
					{upvContent === "" ? (
						<p>{t("PARTICIPANT.DASHBOARD.METRIPORT.PATIENT_SUMMARY_NO_CONTENT")}</p>
					) : upvContent.length > PATIENT_SUMMARY_RENDER_THRESHOLD_CHARS ? (
						<p>{t("PARTICIPANT.DASHBOARD.METRIPORT.PATIENT_SUMMARY_TOO_LONG")}</p>
					) : (
						<div
							className="patient-summary-modal__content"
							style={{
								maxHeight: "70vh",
								overflow: "auto",
								padding: "1rem",
								// Themed surface/text. var(--surface-100) is PrimeReact's
								// light-theme variable, so it stayed white in dark mode.
								backgroundColor: "var(--surface-muted)",
								color: "var(--color-gray-darker)",
								borderRadius: "var(--border-radius)",
							}}
						>
							<ReactMarkdown remarkPlugins={[remarkGfm, remarkBreaks]}>
								{upvContent}
							</ReactMarkdown>
						</div>
					)}
				</>
			)}
		</Dialog>
	);
};

const ModalLayout = ({
	children,
	visible,
	onClose,
}: {
	children: React.ReactNode;
	visible: boolean;
	onClose: () => void;
}) => {
	const { t } = useTranslation("common");

	return (
		<Dialog
			header={t("PARTICIPANT.DASHBOARD.METRIPORT.MODAL_TITLE")}
			visible={visible}
			breakpoints={{ "960px": "50vw", "640px": "100vw" }}
			style={{ width: "40vw" }}
			modal={true}
			draggable={false}
			resizable={false}
			focusOnShow={false}
			onHide={onClose}
		>
			{children}
		</Dialog>
	);
};

export default ParticipantMetriport;
